import { useState } from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import { Home, LogOut } from "lucide-react";
import { Button } from "@/components/ui/button";
import { useAuth } from "@/contexts/AuthContext";
import TrialezeLogo from "./TrialezeLogo";

const Navbar = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const { user, logout } = useAuth();
  const [showUserMenu, setShowUserMenu] = useState(false);
  
  const handleLogout = async () => {
    await logout();
    navigate('/');
  };
  
  return (
    <header className="sticky top-0 z-50 bg-white/90 backdrop-blur-sm border-b border-slate-100" data-testid="navbar">
      <div className="container-shell flex items-center justify-between py-3">
        {/* Logo */}
        <Link to="/" className="flex items-center" data-testid="logo">
          <TrialezeLogo size="default" />
        </Link>
        
        {/* Nav Links */}
        <nav className="hidden md:flex items-center gap-6">
          {user ? (
            // Authenticated nav
            <>
              {[
                { href: "/tools", label: "Tools" },
                { href: "/dashboard", label: "Dashboard" },
                { href: "/reports", label: "Reports" }
              ].map(({ href, label }) => (
                <Link 
                  key={href} 
                  to={href} 
                  className={`text-sm transition-colors ${location.pathname.startsWith(href) ? 'text-blue-600 font-medium' : 'text-slate-600 hover:text-slate-900'}`}
                >
                  {label}
                </Link>
              ))}
            </>
          ) : (
            // Public nav
            <>
              {[
                { href: "/features", label: "All Tools" },
                { href: "/pricing", label: "Pricing" },
                { href: "/solutions", label: "Solutions" },
                { href: "/contact", label: "Contact" }
              ].map(({ href, label }) => (
                <Link 
                  key={href} 
                  to={href} 
                  className={`text-sm transition-colors ${location.pathname === href ? 'text-blue-600 font-medium' : 'text-slate-600 hover:text-slate-900'}`}
                >
                  {label}
                </Link>
              ))}
            </>
          )}
        </nav>
        
        {/* User Actions */}
        <div className="flex items-center gap-3">
          {user ? (
            <div className="relative">
              <button 
                onClick={() => setShowUserMenu(!showUserMenu)}
                className="flex items-center gap-2 px-3 py-2 rounded-lg hover:bg-slate-100 transition-colors"
                data-testid="user-menu-button"
              >
                {user.picture ? (
                  <img src={user.picture} alt={user.name} className="w-8 h-8 rounded-full object-cover" />
                ) : (
                  <div className="w-8 h-8 rounded-full flex items-center justify-center text-white text-sm font-bold" style={{ background: 'linear-gradient(135deg, #2563eb, #3b82f6)' }}>
                    {user.name?.charAt(0) || 'U'}
                  </div>
                )}
                <span className="text-sm font-medium text-slate-700 hidden sm:inline">{user.name?.split(' ')[0]}</span>
              </button>
              
              {showUserMenu && (
                <div 
                  className="absolute right-0 top-full mt-2 w-56 rounded-xl py-2 shadow-lg z-50"
                  style={{ background: '#fff', border: '1px solid #D9E4F2' }}
                >
                  <div className="px-4 py-2 border-b" style={{ borderColor: '#D9E4F2' }}>
                    <p className="text-sm font-medium" style={{ color: '#1E293B' }}>{user.name}</p>
                    <p className="text-xs" style={{ color: '#475569' }}>{user.email}</p>
                  </div>
                  <Link 
                    to="/platform/dashboard" 
                    className="flex items-center gap-2 px-4 py-2 text-sm hover:bg-slate-50"
                    style={{ color: '#475569' }}
                    onClick={() => setShowUserMenu(false)}
                  >
                    <Home className="h-4 w-4" />
                    Platform Dashboard
                  </Link>
                  <button 
                    onClick={handleLogout}
                    className="flex items-center gap-2 w-full px-4 py-2 text-sm hover:bg-slate-50 text-red-600"
                    data-testid="logout-button"
                  >
                    <LogOut className="h-4 w-4" />
                    Sign Out
                  </button>
                </div>
              )}
            </div>
          ) : (
            <>
              <Button 
                variant="outline"
                onClick={() => navigate('/login')}
                className="rounded-lg px-4 py-2 text-sm font-semibold border-slate-200"
                data-testid="login-nav-button"
              >
                Sign In
              </Button>
              <Button 
                onClick={() => navigate('/register')}
                className="rounded-lg px-4 py-2 text-sm font-semibold text-white"
                style={{ background: 'linear-gradient(135deg, #2563eb, #3b82f6)' }}
                data-testid="register-nav-button"
              >
                Get Started
              </Button>
            </>
          )}
        </div>
      </div>
    </header>
  );
};

export default Navbar;
